import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { SecondaryButton } from '../../_core/_ui/buttons.component';
import { SectionHeading } from '../../_core/_ui/typography.component';
import { OtherContactsDisclosure } from '../../PlayerContact/other-contacts-disclosure.component';
import { ContactSummaryList } from '../../PlayerContact/contact-summary-list.component';
import { ParentAddContactButton } from '../../PlayerContact/ParentAddContact/parent-add-contact-button.component';
import { useParentPlayerContacts, useParentPlayers } from '../../../lib/services/parent.service';
import { findPlayerIdByAssessment } from '../../../lib/helpers/parent-players.helpers';
import { PARENT_CONTACT_DETAILS_ROUTE } from '../../../../_configuration';
import { PlayerPageCard } from './player-page-card.component';
import { messages } from './parent-contacts-section.messages';

interface IParentContactsSectionContainerProps {
  playerAssessmentId: string;
}

// Contacts card for a signed-in parent: their own rows, a link into the account
// editor, the collapsed co-contact list and the add-a-co-contact dialog.
// Anonymous visitors get nothing.
export const ParentContactsSectionContainer: React.FC<IParentContactsSectionContainerProps> = ({ playerAssessmentId }) => {
  const history = useHistory();
  const { groups } = useParentPlayers();
  const { contacts } = useParentPlayerContacts();

  const playerId = useMemo(
    () => findPlayerIdByAssessment(groups, playerAssessmentId),
    [groups, playerAssessmentId],
  );
  const mine = useMemo(
    () => (playerId ? contacts.filter((row) => row.playerId === playerId) : []),
    [contacts, playerId],
  );

  if (mine.length === 0) return null;

  // One set per Player: every row shares a playerId, so the first row covers it.
  const otherContacts = mine[0].otherContacts || [];

  return (
    <PlayerPageCard gutterBottom>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
        <SectionHeading><IntlMsg {...messages.sectionTitle} /></SectionHeading>
        <SecondaryButton variant="outlined" size="small" onClick={() => history.push(PARENT_CONTACT_DETAILS_ROUTE)}>
          <IntlMsg {...messages.manageContactDetails} />
        </SecondaryButton>
      </Box>

      <ContactSummaryList contacts={mine} />
      <OtherContactsDisclosure id={playerId || playerAssessmentId} contacts={otherContacts} />

      {/* Scoped to this player's rows, so the dialog's picker opens pre-set to
          the player whose page the parent is already on. */}
      <Box mt={2}>
        <ParentAddContactButton contacts={mine} />
      </Box>
    </PlayerPageCard>
  );
};
